import { supabase } from '../lib/supabase';

export const scheduledPostService = {
  // Get all scheduled posts for the current user with platform details
  async getAll(brandId = null, filterPlatform = null) {
    try {
      if (!supabase) throw new Error('Supabase client not initialized');
      
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      let query = supabase?.from('scheduled_posts')?.select(`
          *,
          brand:brands(id, name),
          platforms:post_platforms(
            id,
            social_account:social_accounts(
              id,
              platform,
              account_name
            )
          )
        `)?.eq('user_id', user?.id)?.order('scheduled_date', { ascending: true });

      if (brandId) {
        query = query?.eq('brand_id', brandId);
      }

      const { data, error } = await query;
      if (error) throw error;

      // Convert snake_case to camelCase and filter by platform if needed
      const posts = data?.map(row => ({
        id: row?.id,
        brandId: row?.brand_id,
        userId: row?.user_id,
        content: row?.content,
        media: row?.media_url,
        mediaAlt: row?.media_alt,
        scheduledDate: new Date(row.scheduled_date),
        status: row?.post_status,
        recurring: row?.is_recurring,
        recurringPattern: row?.recurring_pattern,
        publishedAt: row?.published_at ? new Date(row.published_at) : null,
        failedReason: row?.failed_reason,
        createdAt: new Date(row.created_at),
        updatedAt: new Date(row.updated_at),
        brand: row?.brand,
        platforms: row?.platforms?.map(p => p?.social_account?.platform) || []
      }));

      // Filter by platform if specified
      if (filterPlatform && filterPlatform !== 'all') {
        return posts?.filter(post => post?.platforms?.includes(filterPlatform));
      }

      return posts;
    } catch (error) {
      console.error('Failed to fetch scheduled posts:', error);
      throw error;
    }
  },

  // Create new scheduled post
  async create(postData, platformIds) {
    try {
      if (!supabase) throw new Error('Supabase client not initialized');
      
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Insert the post
      const { data: post, error: postError } = await supabase?.from('scheduled_posts')?.insert({
          brand_id: postData?.brandId,
          user_id: user?.id,
          content: postData?.content,
          media_url: postData?.media || null,
          media_alt: postData?.mediaAlt || null,
          scheduled_date: postData?.scheduledDate,
          post_status: postData?.status || 'scheduled',
          is_recurring: postData?.recurring || false,
          recurring_pattern: postData?.recurringPattern || 'none'
        })?.select()?.single();

      if (postError) throw postError;

      // Link to platforms
      if (platformIds && platformIds?.length > 0) {
        const platformLinks = platformIds?.map(socialAccountId => ({
          post_id: post?.id,
          social_account_id: socialAccountId
        }));

        const { error: linkError } = await supabase?.from('post_platforms')?.insert(platformLinks);

        if (linkError) throw linkError;
      }

      return {
        id: post?.id,
        brandId: post?.brand_id,
        userId: post?.user_id,
        content: post?.content,
        media: post?.media_url,
        mediaAlt: post?.media_alt,
        scheduledDate: new Date(post.scheduled_date),
        status: post?.post_status,
        recurring: post?.is_recurring,
        recurringPattern: post?.recurring_pattern,
        createdAt: new Date(post.created_at),
        updatedAt: new Date(post.updated_at),
        platforms: [] // Will be populated on next fetch
      };
    } catch (error) {
      console.error('Failed to create scheduled post:', error);
      throw error;
    }
  },

  // Update existing post
  async update(postId, updates, platformIds) {
    try {
      if (!supabase) throw new Error('Supabase client not initialized');
      
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const updateData = {};
      if (updates?.content !== undefined) updateData.content = updates?.content;
      if (updates?.media !== undefined) updateData.media_url = updates?.media;
      if (updates?.mediaAlt !== undefined) updateData.media_alt = updates?.mediaAlt;
      if (updates?.scheduledDate !== undefined) updateData.scheduled_date = updates?.scheduledDate;
      if (updates?.status !== undefined) updateData.post_status = updates?.status;
      if (updates?.recurring !== undefined) updateData.is_recurring = updates?.recurring;
      if (updates?.recurringPattern !== undefined) updateData.recurring_pattern = updates?.recurringPattern;

      const { data, error } = await supabase?.from('scheduled_posts')?.update(updateData)?.eq('id', postId)?.eq('user_id', user?.id)?.select()?.single();

      if (error) throw error;

      // Update platform links if provided
      if (platformIds) {
        // Delete existing links
        await supabase?.from('post_platforms')?.delete()?.eq('post_id', postId);

        // Insert new links
        if (platformIds?.length > 0) {
          const platformLinks = platformIds?.map(socialAccountId => ({
            post_id: postId,
            social_account_id: socialAccountId
          }));

          await supabase?.from('post_platforms')?.insert(platformLinks);
        }
      }

      return {
        id: data?.id,
        brandId: data?.brand_id,
        userId: data?.user_id,
        content: data?.content,
        media: data?.media_url,
        mediaAlt: data?.media_alt,
        scheduledDate: new Date(data.scheduled_date),
        status: data?.post_status,
        recurring: data?.is_recurring,
        recurringPattern: data?.recurring_pattern,
        updatedAt: new Date(data.updated_at)
      };
    } catch (error) {
      console.error('Failed to update scheduled post:', error);
      throw error;
    }
  },

  // Delete post
  async delete(postId) {
    try {
      if (!supabase) throw new Error('Supabase client not initialized');
      
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase?.from('scheduled_posts')?.delete()?.eq('id', postId)?.eq('user_id', user?.id);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Failed to delete scheduled post:', error);
      throw error;
    }
  },

  // Duplicate post
  async duplicate(postId) {
    try {
      if (!supabase) throw new Error('Supabase client not initialized');
      
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get original post
      const { data: original, error: fetchError } = await supabase?.from('scheduled_posts')?.select(`
          *,
          platforms:post_platforms(social_account_id)
        `)?.eq('id', postId)?.eq('user_id', user?.id)?.single();

      if (fetchError) throw fetchError;

      // Create duplicate with date +1 day
      const newDate = new Date(original.scheduled_date);
      newDate?.setDate(newDate?.getDate() + 1);

      const { data: duplicate, error: createError } = await supabase?.from('scheduled_posts')?.insert({
          brand_id: original?.brand_id,
          user_id: user?.id,
          content: original?.content,
          media_url: original?.media_url,
          media_alt: original?.media_alt,
          scheduled_date: newDate?.toISOString(),
          post_status: 'scheduled',
          is_recurring: original?.is_recurring,
          recurring_pattern: original?.recurring_pattern
        })?.select()?.single();

      if (createError) throw createError;

      // Duplicate platform links
      if (original?.platforms && original?.platforms?.length > 0) {
        const platformLinks = original?.platforms?.map(p => ({
          post_id: duplicate?.id,
          social_account_id: p?.social_account_id
        }));

        await supabase?.from('post_platforms')?.insert(platformLinks);
      }

      return {
        id: duplicate?.id,
        scheduledDate: new Date(duplicate.scheduled_date)
      };
    } catch (error) {
      console.error('Failed to duplicate post:', error);
      throw error;
    }
  }
};